/* ============================================================
   DS RECENTLY VIEWED — history + rail filler

   Two jobs, one file:

   1. RECORD (product pages). layout/theme.liquid loads this file on
      product templates with `data-product-handle` on the script tag;
      the handle goes to the front of the history in localStorage —
      newest first, no duplicates, the last 12 kept.

   2. FILL (sections/ds-recently-viewed.liquid). `<ds-recently-viewed>`
      waits until it nears the viewport, reads the history, fetches one
      server-rendered card per handle via the Section Rendering API
      (`/products/<handle>?section_id=ds-recently-viewed-card`), keeps
      the first `data-max-items` that come back, inserts them in
      history order and only then removes `hidden`. Nothing to show —
      no history, storage blocked, every product unavailable — and the
      section simply stays hidden.

   Only product handles are stored, only in this browser; nothing is
   sent anywhere beyond the store's own product requests. Every storage
   access is wrapped: private windows and blocked site data must never
   break a product page.

   Not in the DS source — its "Worth another look" is a static
   prototype that always renders. This is the production behaviour its
   README describes ("conditional on browsing history").
   ============================================================ */

(() => {
  const STORAGE_KEY = 'ds:recently-viewed';
  const MAX_STORED = 12;
  const CARD_SECTION = 'ds-recently-viewed-card';

  const read = () => {
    try {
      const value = JSON.parse(localStorage.getItem(STORAGE_KEY));
      return Array.isArray(value) ? value.filter((h) => typeof h === 'string' && h) : [];
    } catch (error) {
      return [];
    }
  };

  const record = (handle) => {
    if (!handle) return;
    try {
      const history = [handle, ...read().filter((h) => h !== handle)].slice(0, MAX_STORED);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
    } catch (error) {
      // Storage unavailable — recently viewed just stays empty.
    }
  };

  // 1. Record — runs once per script tag that carries a handle.
  record(document.currentScript?.dataset.productHandle);

  // 2. Fill — defined once, however many times the file is loaded.
  if (customElements.get('ds-recently-viewed')) return;

  class DsRecentlyViewed extends HTMLElement {
    connectedCallback() {
      // The editor shows placeholder cards instead (see the section).
      if (this.hasAttribute('data-design-mode') || this.loading) return;
      this.loading = true;

      if (!('IntersectionObserver' in window)) {
        this.load();
        return;
      }
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          this.load();
        },
        { rootMargin: '600px 0px' }
      );
      // `hidden` has no box to intersect, so observe the section's
      // always-rendered Shopify wrapper instead.
      observer.observe(this.parentElement || this);
    }

    async load() {
      const max = parseInt(this.dataset.maxItems, 10) || 5;
      // A few spares, so an unavailable product doesn't leave a gap.
      const handles = read().slice(0, max + 3);
      if (!handles.length) return;

      const root = (this.dataset.root || '/').replace(/\/$/, '');
      const cards = await Promise.all(handles.map((handle) => this.fetchCard(root, handle)));
      const found = cards.filter(Boolean).slice(0, max);
      if (!found.length) return;

      const track = this.querySelector('.ds-recently-viewed__track');
      if (!track) return;
      track.replaceChildren(
        ...found.map((card) => {
          const item = document.createElement('div');
          item.className = 'ds-recently-viewed__item';
          item.append(card);
          return item;
        })
      );
      this.hidden = false;
    }

    async fetchCard(root, handle) {
      try {
        const url = `${root}/products/${encodeURIComponent(handle)}?section_id=${CARD_SECTION}`;
        const response = await fetch(url);
        if (!response.ok) return null;
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        return html.querySelector('.ds-shop-card');
      } catch (error) {
        return null;
      }
    }
  }

  customElements.define('ds-recently-viewed', DsRecentlyViewed);
})();
